// @ts-nocheck
import React, { useEffect, useMemo, useRef } from 'react';
import { Group, Mesh, MeshBasicMaterial, BufferGeometry, Float32BufferAttribute, Color } from 'three';
import { useFrame } from '@react-three/fiber';
import { ImprovedNoise } from 'three/examples/jsm/math/ImprovedNoise.js';

type WireBackgroundProps = {
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  /** width/height in world units */
  size?: number;
  /** grid segments */
  segments?: number;
  /** wireframe color */
  color?: string;
};

export default function WireBackground({
  audioRef,
  isPlaying,
  size = 120,
  segments = 64,
  color = '#6b21a8', // purple-800
}: WireBackgroundProps) {
  const groupRef = useRef<Group | null>(null);
  const topRef = useRef<Mesh<BufferGeometry, MeshBasicMaterial> | null>(null);
  const bottomRef = useRef<Mesh<BufferGeometry, MeshBasicMaterial> | null>(null);
  const topMatRef = useRef<MeshBasicMaterial | null>(null);
  const bottomMatRef = useRef<MeshBasicMaterial | null>(null);

  // Smoothed play state factor (0..1), drives intensity when playing vs paused
  const playFactorRef = useRef<number>(isPlaying ? 1 : 0);

  // Noise
  const noise = useMemo(() => new ImprovedNoise(), []);

  // Build plane base positions to displace from
  const basePositionsTop = useRef<Float32Array | null>(null);
  const basePositionsBottom = useRef<Float32Array | null>(null);

  // No audio analyser; we ignore the audioRef on purpose to avoid playback issues

  // Capture base positions after geometry mounts
  useEffect(() => {
    const t = topRef.current?.geometry;
    const b = bottomRef.current?.geometry;
    if (t && !basePositionsTop.current) {
      basePositionsTop.current = (t.getAttribute('position') as Float32BufferAttribute).array.slice() as Float32Array;
    }
    if (b && !basePositionsBottom.current) {
      basePositionsBottom.current = (b.getAttribute('position') as Float32BufferAttribute).array.slice() as Float32Array;
    }
  }, []);

  // Simple frame skip to halve geometry update cost
  const flipRef = useRef(false);

  useFrame((_, delta) => {
    // gentle spin
    if (groupRef.current && isPlaying) {
      groupRef.current.rotation.y += 0.05 * delta;
    }

    const time = performance.now();

    // Smooth play factor (0..1) for transitions when starting/stopping playback
    const target = isPlaying ? 0 : 1;
    const lambda = 1 - Math.exp(-delta / 0.8); // ~0.8s time constant
    playFactorRef.current += (target - playFactorRef.current) * lambda;
    const playFactor = playFactorRef.current;

    // Subtle time-based pulse so there are peaks even without analyser
    const pulse = 0.9 + 0.1 * (0.5 + 0.5 * Math.sin(time * 0.003));

    // Distortion: when playing, reduce peaks to flatten the effect
    const baseDistortion = 1.3;
    const intensity = baseDistortion * pulse * (1 - 0.6 * playFactor);
    const distortTop = intensity;
    const distortBottom = intensity;

    // Color: white base, slight tint towards violet; less saturated while playing
    const sat = Math.min(0.4, 0.05 + 0.35 * (1 - playFactor));
    const hue = 0.76; // violet hue
    const light = 0.95;
    const colorHsl = new Color().setHSL(hue, sat, light);
    if (topMatRef.current) topMatRef.current.color.copy(colorHsl);
    if (bottomMatRef.current) bottomMatRef.current.color.copy(colorHsl);

    // Skip every other frame to reduce CPU load
    flipRef.current = !flipRef.current;
    if (flipRef.current) return;

    // Update a plane's z displacement based on noise and distortion factor
    const updatePlane = (
      mesh: Mesh<BufferGeometry, MeshBasicMaterial> | null,
      base: React.MutableRefObject<Float32Array | null>,
      distortion: number
    ) => {
      if (!mesh) return;
      const geom = mesh.geometry;
      const pos = geom.getAttribute('position') as Float32BufferAttribute;
      const arr = pos.array as Float32Array;
      const baseArr = base.current;
      if (!baseArr) return;

      // Plane geometry is in XY; we modulate Z
      for (let i = 0; i < arr.length; i += 3) {
        const x = baseArr[i + 0];
        const y = baseArr[i + 1];
        const z0 = baseArr[i + 2];

        // Use a single 3D noise sample (cheaper than combining two)
        const n = noise.noise(x * 0.1, y * 0.1, time * 0.0003);
        const amp = 1.2; // amplitude in world units (reduced for subtlety)
        const z = z0 + n * distortion * amp;

        arr[i + 0] = x;
        arr[i + 1] = y;
        arr[i + 2] = z;
      }
      pos.needsUpdate = true;
      geom.computeVertexNormals();
    };

    updatePlane(topRef.current, basePositionsTop, distortTop);
    updatePlane(bottomRef.current, basePositionsBottom, distortBottom);
  });

  const planeArgs = useMemo<[number, number, number, number]>(() => [size, size, segments, segments], [size, segments]);

  return (
    <group ref={groupRef}>
      {/* Top grid */}
      <mesh ref={topRef} rotation={[-Math.PI * 0.5, 0, 0]} position={[0, 14, 0]}>
        <planeGeometry args={planeArgs} />
        <meshBasicMaterial ref={topMatRef} color="white" wireframe side={2} toneMapped={false} transparent opacity={0.12} />
      </mesh>
      {/* Bottom grid */}
      <mesh ref={bottomRef} rotation={[-Math.PI * 0.5, 0, 0]} position={[0, -12, 0]}>
        <planeGeometry args={planeArgs} />
        <meshBasicMaterial ref={bottomMatRef} color="white" wireframe side={2} toneMapped={false} transparent opacity={0.2} />
      </mesh>
    </group>
  );
}

 

